import { useCallback, useState } from 'react';
import { message } from 'antd';
import { useToken } from '@/common/helper';
import { useUnAuthorize } from '@/hooks';
import { GetActivityEventTypes, type EventTypeGroupDTO } from '@/apis/activity';
import type { ActivityEventTypeId, EventTypeGroup } from '../../types';

const toEventTypeGroup = (group: EventTypeGroupDTO): EventTypeGroup => ({
  id: group.value,
  label: group.label,
  children: group.children?.map((child) => ({
    id: child.value as ActivityEventTypeId,
    label: child.label,
  })),
});

export interface UseFetchActivityEventTypesResult {
  eventTypeGroups: EventTypeGroup[];
  allEventTypeGroups: EventTypeGroup[];
  fetchEventTypes: (search?: string) => Promise<EventTypeGroup[]>;
}

const useFetchActivityEventTypes = (): UseFetchActivityEventTypesResult => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const [eventTypeGroups, setEventTypeGroups] = useState<EventTypeGroup[]>([]);
  const [allEventTypeGroups, setAllEventTypeGroups] = useState<
    EventTypeGroup[]
  >([]);

  const fetchEventTypes = useCallback(
    async (search = '') => {
      try {
        const res = await GetActivityEventTypes(token, search);
        const data = res?.data;
        if (data?.data?.is_success) {
          const groups = (data.data.data ?? []).map(toEventTypeGroup);
          setEventTypeGroups(groups);
          if (!search.trim()) setAllEventTypeGroups(groups);
          return groups;
        }
        message.error(data?.data?.message);
        return [];
      } catch (error) {
        unauthorize(error);
        return [];
      }
    },
    [token, unauthorize]
  );

  return { eventTypeGroups, allEventTypeGroups, fetchEventTypes };
};

export default useFetchActivityEventTypes;
